import ExportButton from "components/Button/ExportButton/ExportButton";
import SearchResult from "features/SearchResultsList/components/SearchResult";
import { useSaveBookmarkModal } from "features/Bookmarks";
import { Paper } from "helpers/api";
import pluralize from "helpers/pluralize";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import { PaperCiteModal } from "hooks/usePaperCite/PaperCiteModal";
import usePaperCite from "hooks/usePaperCite/usePaperCite";
import { useUploadedPapers } from "./hooks/useUploadedPapers";
import ZeroUploadedPapersEducation from "./ZeroUploadedPapersEducation";
import { isUploadedPaper } from "./utils/isUploadedPaper";

// Helper function to check if a paper is a fake uploading paper
const isFakeUploadingPaper = (paper: Paper): boolean => {
  // Check key properties that identify a fake uploading paper
  return (
    !paper.paper_id &&
    !paper.display_text &&
    paper.citation_count === 0 &&
    paper.title?.startsWith("Uploading: ")
  );
};

export const ResearchHubUploadedPaperResults = () => {
  const { papers, isLoadingPaper, isLoading } = useUploadedPapers();
  const [labels] = useLabels("research-hub");
  const {
    openSaveBookmarkModalForUploadedPaper,
    openSaveBookmarkModalForPaper,
  } = useSaveBookmarkModal();
  const { isCiteModalOpen, citeData, openCiteModal, closeCiteModal } =
    usePaperCite();
  const noUploadedPapers = papers.length === 0 && !isLoading;

  const downloadablePapers: Paper[] = papers.map((paper) => ({
    paper_id: paper.paper_id,
    title: paper.title,
    authors: paper.authors ?? [],
    badges: paper.badges ?? {},
    citation_count: paper.citation_count ?? 0,
    display_text: paper.display_text ?? "",
    doc_id: paper.doc_id ?? "",
    doi: paper.doi ?? "",
    has_valid_chat_pdf: paper.has_valid_chat_pdf ?? false,
    journal: paper.journal ?? "",
    pages: paper.pages ?? "",
    primary_author:
      paper.primary_author ?? (paper.authors && paper.authors[0]) ?? "",
    url_slug: paper.url_slug ?? "",
    volume: paper.volume ?? "",
    year: paper.year ?? new Date().getFullYear(),
    abstract: paper.display_text ?? "",
    abstract_takeaway: paper.display_text ?? "",
  }));

  const handleCite = (paper: Paper) => {
    openCiteModal(paper);
  };

  const handleSave = (paper: Paper) => {
    if (!paper || !paper.paper_id) return;

    if (isUploadedPaper(paper.paper_id)) {
      openSaveBookmarkModalForUploadedPaper(paper.paper_id);
    } else {
      openSaveBookmarkModalForPaper(paper.paper_id);
    }
  };

  return (
    <div className="justify-between items-center rounded-lg">
      <div className="flex justify-between items-center pb-3 border-b">
        <div className="flex gap-2 items-center">
          <div className="font-bold text-fg-base">
            {noUploadedPapers ? (
              labels["no-uploads"]
            ) : (
              <>
                {papers.length}{" "}
                {pluralize(labels["upload"], { count: papers.length })}
              </>
            )}
          </div>
        </div>
        <div data-testid="export-button-wrapper">
          <ExportButton
            items={downloadablePapers}
            title="Research Hub Papers"
            disabled={
              isLoadingPaper ||
              downloadablePapers.length === 0 ||
              noUploadedPapers
            }
            skipPapersFetch={true}
            actionSource={MixpanelActionSource.ResearchHub}
          />
        </div>
      </div>
      <div>
        {noUploadedPapers ? (
          <ZeroUploadedPapersEducation />
        ) : (
          papers.map((paper: Paper, index: number) => (
            <SearchResult
              key={paper.paper_id}
              index={index + 1}
              item={paper}
              hashPaperRecordId={paper.paper_id}
              isLoadingExtractedAnswer={isLoadingPaper && !paper.display_text}
              isLoadingTitle={isLoadingPaper && !paper.title}
              isProcessingMetadata={
                isFakeUploadingPaper(paper) || (isLoadingPaper && !paper.title)
              }
              isUploading={isLoadingPaper && !paper.display_text}
              onClickCite={() => handleCite(paper)}
              onClickSave={() => handleSave(paper)}
              showCiteBtn={true}
              showSaveBtn={true}
              showShareBtn={false}
              showManageUploadedPaperBtn={true}
            />
          ))
        )}
      </div>
      {citeData && (
        <PaperCiteModal
          data={citeData}
          open={isCiteModalOpen}
          onClose={closeCiteModal}
        />
      )}
    </div>
  );
};

export default ResearchHubUploadedPaperResults;
